export const MAX_FILE_MB = 100
export const MAX_SIGNATURE_PIXELS = 25_000_000
export const MAX_SIGNATURE_OUTPUT_PIXELS = 4_000_000
export const MAX_SELECTED_PAGES = 200

export interface AsyncOperationGuard {
  activate: () => void
  deactivate: () => void
  start: () => number
  isCurrent: (token: number) => boolean
}

export function createAsyncOperationGuard(): AsyncOperationGuard {
  let active = false
  let generation = 0

  return {
    activate() { active = true },
    deactivate() { active = false; generation += 1 },
    start() { generation += 1; return generation },
    isCurrent(token) { return active && token === generation },
  }
}

export function parsePageSelection(value: string, totalPages: number): Set<number> | null {
  const selected = new Set<number>()
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean)
  if (parts.length === 0) return null

  for (const part of parts) {
    const match = /^(\d+)(?:\s*-\s*(\d+))?$/.exec(part)
    if (!match) return null
    const start = Number(match[1])
    const end = Number(match[2] ?? match[1])
    if (start < 1 || end < start || end > totalPages) return null
    if (end - start + 1 > MAX_SELECTED_PAGES) return null
    for (let page = start; page <= end; page += 1) {
      selected.add(page - 1)
      if (selected.size > MAX_SELECTED_PAGES) return null
    }
  }

  return selected.size <= MAX_SELECTED_PAGES ? selected : null
}

export function validateSignatureDimensions(width: number, height: number): 'invalid_dimensions' | 'too_many_pixels' | null {
  if (width < 2 || height < 2 || width > 10000 || height > 10000) return 'invalid_dimensions'
  if (width * height > MAX_SIGNATURE_PIXELS) return 'too_many_pixels'
  return null
}

export function fitSignatureDimensions(width: number, height: number, maxPixels = MAX_SIGNATURE_OUTPUT_PIXELS): SignatureImageDimensions {
  if (width * height <= maxPixels) return { width, height }
  const scale = Math.sqrt(maxPixels / (width * height))
  return {
    width: Math.max(2, Math.floor(width * scale)),
    height: Math.max(2, Math.floor(height * scale)),
  }
}

export interface SignatureImageDimensions {
  width: number
  height: number
}

const JPEG_START_OF_FRAME_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
  0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
])

/** Reads dimensions from PNG/JPEG metadata without invoking an image decoder. */
export function parseSignatureImageDimensions(
  bytes: ArrayBuffer,
  mimeType: string,
): SignatureImageDimensions | null {
  const data = new DataView(bytes)

  if (mimeType === 'image/png') {
    if (data.byteLength < 24) return null
    const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
    if (pngSignature.some((value, index) => data.getUint8(index) !== value)) return null
    if (data.getUint32(8) !== 13 || data.getUint32(12) !== 0x49484452) return null
    return { width: data.getUint32(16), height: data.getUint32(20) }
  }

  if (mimeType !== 'image/jpeg' || data.byteLength < 4 || data.getUint16(0) !== 0xffd8) return null

  let offset = 2
  while (offset < data.byteLength) {
    if (data.getUint8(offset) !== 0xff) return null
    while (offset < data.byteLength && data.getUint8(offset) === 0xff) offset += 1
    if (offset >= data.byteLength) return null

    const marker = data.getUint8(offset)
    offset += 1
    if (marker === 0xd9 || marker === 0xda) return null
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue
    if (offset + 2 > data.byteLength) return null

    const segmentLength = data.getUint16(offset)
    if (segmentLength < 2 || offset + segmentLength > data.byteLength) return null
    if (JPEG_START_OF_FRAME_MARKERS.has(marker)) {
      if (segmentLength < 7) return null
      return { width: data.getUint16(offset + 5), height: data.getUint16(offset + 3) }
    }
    offset += segmentLength
  }

  return null
}

export function detectSignatureImageMime(
  bytes: ArrayBuffer,
  fileName: string,
  declaredMimeType: string,
): 'image/png' | 'image/jpeg' | null {
  const data = new DataView(bytes)
  if (data.byteLength >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => data.getUint8(index) === value)) {
    return 'image/png'
  }
  if (data.byteLength >= 2 && data.getUint16(0) === 0xffd8) return 'image/jpeg'
  if (declaredMimeType === 'image/png' || declaredMimeType === 'image/jpeg') return declaredMimeType
  const extension = fileName.toLowerCase().split('.').pop()
  return extension === 'png' ? 'image/png' : extension === 'jpg' || extension === 'jpeg' ? 'image/jpeg' : null
}

export interface SignaturePlacement {
  x: number
  y: number
  width: number
  height: number
}

interface PdfPlacement extends SignaturePlacement { rotation: 0 | 90 | 180 | 270 }

export interface PdfPageBox {
  x: number
  y: number
  width: number
  height: number
}

/** Returns the visible PDF page area shared by the MediaBox and CropBox. */
export function intersectPdfPageBoxes(first: PdfPageBox, second: PdfPageBox): PdfPageBox | null {
  const left = Math.max(first.x, second.x)
  const bottom = Math.max(first.y, second.y)
  const right = Math.min(first.x + first.width, second.x + second.width)
  const top = Math.min(first.y + first.height, second.y + second.height)
  if (right <= left || top <= bottom) return null
  return { x: left, y: bottom, width: right - left, height: top - bottom }
}

/** Fits the signature into the shared normalized box without changing its source aspect ratio. */
export function containSignaturePlacement(
  box: SignaturePlacement,
  viewportWidth: number,
  viewportHeight: number,
  signatureAspectRatio: number,
): SignaturePlacement {
  const boxWidth = box.width * viewportWidth
  const boxHeight = box.height * viewportHeight
  const boxAspectRatio = boxWidth / boxHeight

  if (signatureAspectRatio >= boxAspectRatio) {
    const containedHeight = boxWidth / signatureAspectRatio
    const normalizedHeight = containedHeight / viewportHeight
    return {
      x: box.x,
      y: box.y + (box.height - normalizedHeight) / 2,
      width: box.width,
      height: normalizedHeight,
    }
  }

  const containedWidth = boxHeight * signatureAspectRatio
  const normalizedWidth = containedWidth / viewportWidth
  return {
    x: box.x + (box.width - normalizedWidth) / 2,
    y: box.y,
    width: normalizedWidth,
    height: box.height,
  }
}

/** Maps the top-left, rotated pdf.js viewport coordinates back to PDF user space. */
export function previewPlacementToPdf(
  placement: SignaturePlacement,
  pageBox: PdfPageBox,
  pageRotation: number,
): PdfPlacement {
  const rotation = (((pageRotation % 360) + 360) % 360) as 0 | 90 | 180 | 270
  const { x: pageX, y: pageY, width: pageWidth, height: pageHeight } = pageBox
  const { x, y, width, height } = placement
  switch (rotation) {
    case 90:
      return {
        x: pageX + y * pageWidth,
        y: pageY + (x + width) * pageHeight,
        width: width * pageHeight,
        height: height * pageWidth,
        rotation,
      }
    case 180:
      // pdf.js keeps viewport Y aligned with PDF Y at 180°, while pdf-lib's
      // -180° image rotation extends down from its anchor, hence y + height.
      return {
        x: pageX + (1 - x) * pageWidth,
        y: pageY + (y + height) * pageHeight,
        width: width * pageWidth,
        height: height * pageHeight,
        rotation,
      }
    case 270:
      return {
        x: pageX + (1 - y) * pageWidth,
        y: pageY + (1 - x - width) * pageHeight,
        width: width * pageHeight,
        height: height * pageWidth,
        rotation,
      }
    default:
      return {
        x: pageX + x * pageWidth,
        y: pageY + (1 - y - height) * pageHeight,
        width: width * pageWidth,
        height: height * pageHeight,
        rotation: 0,
      }
  }
}

export function signedFilename(name: string): string {
  return `${name.replace(/\.pdf$/i, '')}-signed.pdf`
}

export async function signPdf(
  file: File,
  png: ArrayBuffer,
  placements: Map<number, SignaturePlacement>,
): Promise<Blob> {
  const { PDFDocument, degrees } = await import('pdf-lib')
  const document = await PDFDocument.load(await file.arrayBuffer())
  const image = await document.embedPng(png)

  for (const [pageIndex, placement] of placements) {
    const page = document.getPage(pageIndex)
    const visibleBox = intersectPdfPageBoxes(page.getCropBox(), page.getMediaBox())
    if (!visibleBox) throw new Error('invalid_page_boxes')
    const pageRotation = ((page.getRotation().angle % 360) + 360) % 360
    const viewportWidth = pageRotation === 90 || pageRotation === 270 ? visibleBox.height : visibleBox.width
    const viewportHeight = pageRotation === 90 || pageRotation === 270 ? visibleBox.width : visibleBox.height
    const contained = containSignaturePlacement(
      placement,
      viewportWidth,
      viewportHeight,
      image.width / image.height,
    )
    const mapped = previewPlacementToPdf(contained, visibleBox, pageRotation)
    page.drawImage(image, { ...mapped, rotate: degrees(-mapped.rotation) })
  }

  return new Blob([await document.save()], { type: 'application/pdf' })
}

export function canvasPng(canvas: HTMLCanvasElement): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => {
    if (!blob) reject(new Error('signature_image_failed'))
    else blob.arrayBuffer().then(resolve, reject)
  }, 'image/png'))
}

export function fitTypedSignatureFontSize(
  text: string,
  measureWidth: (value: string, fontSize: number) => number,
  maxWidth: number,
  initialFontSize = 96,
  minimumFontSize = 24,
): number {
  const measuredWidth = measureWidth(text, initialFontSize)
  if (!Number.isFinite(measuredWidth) || measuredWidth <= maxWidth) return initialFontSize
  return Math.max(minimumFontSize, Math.floor(initialFontSize * maxWidth / measuredWidth))
}

export async function typedSignature(text: string): Promise<ArrayBuffer> {
  const canvas = document.createElement('canvas')
  canvas.width = 900
  canvas.height = 240
  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas_unavailable')
  const maxTextWidth = canvas.width - 96
  const fontSize = fitTypedSignatureFontSize(text, (value, size) => {
    context.font = `italic ${size}px cursive`
    return context.measureText(value).width
  }, maxTextWidth)
  context.font = `italic ${fontSize}px cursive`
  context.fillStyle = '#111827'
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText(text, canvas.width / 2, canvas.height / 2, maxTextWidth)
  return canvasPng(canvas)
}
